import HeaderNotificationsControl from '@/ui/layouts/authenticatedPageWrapper/components/HeaderNotificationsControl';
import UserLogoutSection from '@/ui/layouts/authenticatedPageWrapper/components/UserLogoutSection';
import { useChatAgent } from '@/ui/components/chatAgent/ChatAgentContextProvider';
import { getBreakpointPx } from '@/hooks/useIsContainerBeyondBreakpoint';
import {
  Breadcrumb,
  BreadcrumbItem,
  Button,
  MenuItemCore,
  SidebarCore,
} from '@payaca/ambrosia';
import ShineBorder from '@payaca/components/shineBorder/ShineBorder';
import { useLocalStorage, useMeasure } from '@payaca/hooks';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { AnimatePresence, motion } from 'motion/react';
import React, { FC, PropsWithChildren, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import CompanyDropdown from './components/CompanyDropdown';
import NavigationSidebar from './components/NavigationSidebar';

export type TProps = {
  className?: string;
  breadcrumbItems?: BreadcrumbItem[];
  scrollLock?: boolean;
};

const AuthenticatedPageWrapper: FC<PropsWithChildren<TProps>> = (props) => {
  const {
    className,
    breadcrumbItems = [],
    children,
    scrollLock = false,
  } = props;

  const [measureRef, { width: mainContentWidth }] =
    useMeasure<HTMLDivElement>();
  // The sidebar detaches at the same width that `max-w-screen-xl` caps page
  // content.
  const [smallContentThreshold] = useState(() => getBreakpointPx('xl'));

  const {
    show: isChatAgentOpen,
    hasChatAgentAccess,
    showChatAgent,
    hideChatAgent,
  } = useChatAgent();
  const location = useLocation();

  const [sidebarPreference, setSidebarPreference] = useLocalStorage<
    'attached' | 'detached'
  >('payaca:sidebar-mode', 'attached');
  // Caches the last computed effective mode so it's available immediately on
  // remount before useMeasure fires. Necessary because this component remounts
  // on every page navigation — can be removed once sidebar rendering moves to
  // AuthenticatedRootLayout (which stays mounted across navigations).
  const [lastEffectiveMode, setLastEffectiveMode] = useLocalStorage<
    'attached' | 'detached'
  >('payaca:sidebar-effective-mode', 'attached');
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Close drawer on navigation
  useEffect(() => {
    setDrawerOpen(false);
  }, [location.pathname]);

  // When content is too narrow, force detached mode regardless of preference.
  // Before measurement arrives, use the last known effective mode to avoid
  // sidebar flash on page navigation (component remounts, useMeasure starts at 0).
  const hasMeasured = mainContentWidth > 0;
  const isSmallContent =
    hasMeasured &&
    smallContentThreshold !== null &&
    mainContentWidth < smallContentThreshold;
  const computedMode = isSmallContent ? 'detached' : sidebarPreference;
  const effectiveMode = hasMeasured ? computedMode : lastEffectiveMode;

  useEffect(() => {
    if (hasMeasured) setLastEffectiveMode(computedMode);
  }, [hasMeasured, computedMode, setLastEffectiveMode]);

  const toggleSidebar = () => {
    if (!isSmallContent && effectiveMode === 'attached') {
      setSidebarPreference('detached');
    } else {
      setDrawerOpen((prev) => !prev);
    }
  };

  return (
    <>
      <div
        ref={measureRef}
        className={clstx('grid h-full overflow-hidden', className)}
        style={{
          gridTemplateRows: '1fr',
          gridTemplateColumns: 'auto minmax(0, 1fr)',
          gridTemplateAreas: '"sidebar content"',
        }}
      >
        {/*Sidebar (desktop, attached)*/}
        <AnimatePresence initial={false}>
          {effectiveMode === 'attached' && (
            <motion.aside
              initial={{ width: 0 }}
              animate={{ width: 240 }}
              exit={{ width: 0 }}
              transition={{ duration: 0.2, ease: 'easeInOut' }}
              // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values
              className="hidden md:grid p-4 pr-2 min-h-0 grid-rows-[auto_1fr_auto] overflow-hidden"
              style={{ gridArea: 'sidebar' }}
            >
              <SidebarContent />
            </motion.aside>
          )}
        </AnimatePresence>

        {/*Sidebar (drawer — desktop detached & mobile)*/}
        <SidebarCore
          side="left"
          size="sm"
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          className="max-h-full grow"
        >
          <div className={clstx('flex flex-col h-full bg-gray-a8 py-4 px-4')}>
            {effectiveMode === 'detached' && !isSmallContent && (
              <div className="hidden md:flex mb-4 p-2 pb-6 border-b">
                <Button
                  variant="secondary"
                  size="sm"
                  className="aspect-square"
                  onClick={() => {
                    setSidebarPreference('attached');
                    setDrawerOpen(false);
                  }}
                >
                  <UntitledIcon name="align-right-01.3" className="size-4" />
                </Button>
              </div>
            )}
            <SidebarContent />
          </div>
        </SidebarCore>

        {/*Main*/}
        <div
          className={clstx(
            'grid min-w-0 min-h-0 m-2 bg-white rounded-lg border overflow-hidden',
            isChatAgentOpen && 'mr-0'
          )}
          style={{ gridArea: 'content', gridTemplateRows: 'auto 1fr' }}
        >
          {/*Header*/}
          <header className="flex justify-between items-center gap-4 p-3 bg-white border-b border-gray-200">
            <Button
              variant="secondary"
              size="sm"
              className="aspect-square"
              onClick={toggleSidebar}
            >
              <UntitledIcon name="layout-left.3" className="size-4" />
            </Button>

            <Breadcrumb
              className="grow"
              items={breadcrumbItems}
              LinkComponent={Link}
            />

            {hasChatAgentAccess && (
              <Button
                className="group relative"
                variant="secondary"
                size="sm"
                onClick={isChatAgentOpen ? hideChatAgent : showChatAgent}
              >
                <ShineBorder
                  className="opacity-0 group-hover:opacity-100 transition-opacity duration-700"
                  borderWidth={2}
                  duration={5}
                  shineColor={['var(--color-primary)']}
                />
                <UntitledIcon className="size-4" name="star-05.3" />
                Agent Dave
              </Button>
            )}
          </header>

          <motion.main
            id="main-page-content"
            layoutScroll={!scrollLock}
            className={clstx(
              'overflow-y-auto min-h-0 @container',
              scrollLock && 'overflow-hidden'
            )}
          >
            {children}
          </motion.main>
        </div>
      </div>
    </>
  );
};

export default AuthenticatedPageWrapper;

const SidebarContent: FC = () => {
  const { showChatAgent } = useChatAgent();

  return (
    <>
      <div className="flex items-center gap-2 mb-4 justify-between">
        <CompanyDropdown />
        <HeaderNotificationsControl />
      </div>

      <NavigationSidebar />

      <div className="mt-auto">
        <div className="space-y-1 mb-4">
          <MenuItemCore iconName="help-square.3" onClick={showChatAgent}>
            Help & support
          </MenuItemCore>

          <MenuItemCore
            as="a"
            href="https://payaca.com/updates"
            target="_blank"
            rel="noopener noreferrer"
            iconName="star-01.3"
            wrapperClassName={clstx(
              'no-underline text-blue-900 font-normal',
              'hover:not-data-disabled:bg-gray-200 hover:not-disabled:bg-gray-200'
            )}
          >
            What's new?
          </MenuItemCore>
        </div>

        <UserLogoutSection />
      </div>
    </>
  );
};
